import { deepCopy } from "../../../../utils/objUtils";
import { stateT } from "../../types";
import {
  IObject,
  IProduct,
  IProjectCategory,
  ISelectItemObjectData,
  ISelectObjectOptions,
  optionObjectOptionT,
} from "../uiTypes";

export const getActiveObjectId = (state: stateT): string | null => {
  const { activeObjectId } = state.ui;
  return activeObjectId;
};

export const getObjects = (state: stateT) => {
  const { objects } = state.ui;
  return objects;
};

export const getCurrentCategoryFolder = (state: stateT): IObject | null => {
  const { currentCategoryFolder } = state.ui;
  return currentCategoryFolder;
};

export const getCurrentFolder = (state: stateT): IObject | null => {
  const { activeFolder } = state.ui;
  return activeFolder;
};

export const getPrevFolder = (state: stateT): IObject | null => {
  const currentFolder = getCurrentFolder(state);
  if (!currentFolder) return null;

  const parent = currentFolder.parent;
  if (!parent) return null;
  return parent;
};

// Safer variant: derives parent from the global tree when `parent` is missing
export const getPrevFolderSafe = (state: stateT): IObject | null => {
  const current = getCurrentFolder(state);

  if (!current) return null;
  if (current.parent) return current.parent;

  const objects = getObjects(state);
  const stack: IObject[] = [...objects];

  while (stack.length) {
    const node = stack.pop()!;
    const children = node.children || [];
    for (const child of children) {
      if (child.id === current.id) {
        return node;
      }

      stack.push(child);
    }
  }

  return null;
};

export const getFolders = (state: stateT) => {
  const currentObject = getCurrentFolder(state);
  if (!currentObject) {
    const objects = getObjects(state);
    return objects;
  }
  return currentObject.children;
};

export const getProjectsFromCurrentObject = (state: stateT) => {
  // Prefer currentCategoryFolder (which has fetched products) over activeFolder
  const currentCategoryFolder = getCurrentCategoryFolder(state);
  if (currentCategoryFolder && currentCategoryFolder.projects) {
    return currentCategoryFolder.projects;
  }

  const currentObject = getCurrentFolder(state);
  if (!currentObject) {
    return [];
  }
  return currentObject.projects || [];
};

export const getPathToRoot = (state: stateT): IObject[] => {
  const currentObject = getCurrentFolder(state);
  if (!currentObject) {
    return [];
  }

  const path: IObject[] = [];
  let current: IObject | null = currentObject;

  while (current) {
    path.unshift(current);
    current = current.parent || null;
  }

  return path;
};

export const getPathToRootString = (state: stateT): string => {
  const path = getPathToRoot(state);
  if (!path.length) {
    return "";
  }

  return path.map(obj => obj.name).join("/");
};

export const getProductFromCurrentFolder =
  (projectId: number, id: number) =>
  (state: stateT): IProduct | null => {
    const currentObject = getCurrentFolder(state);
    if (!currentObject) {
      return null;
    }

    const project = currentObject.projects.find(
      project => project.id === projectId
    );
    if (!project) {
      return null;
    }

    const product = project.availableProducts.find(
      product => product.id === id
    );
    return product || null;
  };

export const getProjectFromCurrentFolder =
  (projectId: number) =>
  (state: stateT): IProjectCategory | null => {
    const currentObject = getCurrentFolder(state);
    if (!currentObject) {
      return null;
    }

    const project = currentObject.projects.find(
      project => project.id === projectId
    );
    return project || null;
  };

export const getSelectDataObjects = (state: stateT) => {
  const { selectObjects } = state.ui;
  return selectObjects;
};
export const getSelectObjectDataById = (id: string) => (state: stateT) => {
  const selectObjects = getSelectDataObjects(state);
  return selectObjects[id] || null;
};

export const getSelectOptionObjectDataById =
  (productId: string, type: optionObjectOptionT) => (state: stateT) => {
    const selectObjectData = getSelectObjectDataById(productId)(state);
    if (!selectObjectData) {
      return null;
    }
    if (type === "material") {
      return selectObjectData.materialOptions;
    }

    return selectObjectData.geometryOptions;
  };

export const getSelectOptionObjectDataByProxyId =
  (productId: string, type: optionObjectOptionT) =>
  (proxyId: number) =>
  (state: stateT) => {
    const selectObjectData = getSelectOptionObjectDataById(
      productId,
      type
    )(state);
    if (!selectObjectData) {
      return null;
    }
    return selectObjectData[proxyId] || null;
  };

export const getSelectOptionObjectDataByOptionId =
  (productId: string, type: optionObjectOptionT) =>
  (proxyId: number, optionId: number) =>
  (state: stateT) => {
    const selectOptionData = getSelectOptionObjectDataByProxyId(
      productId,
      type
    )(proxyId)(state);
    if (!selectOptionData) {
      return null;
    }
    return selectOptionData[optionId] || null;
  };

export const getProjectByIdAndPath =
  (id: number, path: number[]) =>
  (state: stateT): IProjectCategory | null => {
    const objects = getObjects(state);
    let currentObject: IObject | null = null;

    for (const obj of objects) {
      if (obj.id === path[0]) {
        currentObject = obj;
        break;
      }
    }

    if (!currentObject) {
      return null;
    }

    for (let i = 1; i < path.length; i++) {
      if (!currentObject) continue;
      const nextObject: IObject | undefined = (currentObject.children || []).find(
        obj => obj.id === path[i]
      );
      if (!nextObject) {
        return null;
      }
      currentObject = nextObject;
    }

    const project = (currentObject.projects || []).find(project => project.id === id);

    return project || null;
  };

export const getProductByIdAndPath =
  (id: number, path: number[], projectId: number) =>
  (state: stateT): IProduct | null => {
    const project = getProjectByIdAndPath(projectId, path)(state);

    const product = project?.availableProducts?.find(
      product => product.id === id
    );

    return product || null;
  };

/**
 * Get product from cache by projectId and productId
 */
export const getProductFromCache =
  (projectId: number, id: number) =>
  (state: stateT): IProduct | null => {
    const list = state.ui.productsCache[projectId] || [];
    const product = list.find(p => p.id === id) || null;
    return product;
  };

/**
 * Get list of products for a project from cache
 */
export const getProductsFromCacheByProject =
  (projectId: number) =>
  (state: stateT): IProduct[] => {
    return state.ui.productsCache[projectId] || [];
  };

export const getProductById =
  (id: string) =>
  (state: stateT): IProduct | null => {
    const selectData = getSelectObjectDataById(id)(state);
    if (!selectData) {
      return null;
    }

    const realId = selectData.realId;
    const projectId = selectData.projectId;

    // 1) Try cache first
    const fromCache = getProductFromCache(projectId, realId)(state);
    if (fromCache) return fromCache;

    // 2) Try to resolve by current folder path
    const byPath = getProductByIdAndPath(
      realId,
      selectData.pathObjectIds,
      projectId
    )(state);

    return byPath;
  };

export const getUpdateSelectDataByIdAndNewData =
  (id: string, data: Partial<ISelectItemObjectData>) =>
  (state: stateT): ISelectItemObjectData => {
    const selectData = getSelectObjectDataById(id)(state);

    if (!selectData) {
      throw new Error(`Select data with id ${id} not found`);
    }

    return getUpdateSelectDataByNewData(selectData, data)(state);
  };

export const getUpdateSelectDataByNewData =
  (
    selectData: ISelectItemObjectData,
    updSelectData: Partial<ISelectItemObjectData>
  ) =>
  (state: stateT): ISelectItemObjectData => {
    const resData: ISelectItemObjectData = deepCopy({
      ...selectData,
      ...updSelectData,
    });

    if (updSelectData.materialOptions) {
      resData.materialOptions = getUpdateSelectOptionsDataByNewData(
        selectData,
        "material",
        updSelectData.materialOptions
      )(state);
    }

    if (updSelectData.geometryOptions) {
      resData.geometryOptions = getUpdateSelectOptionsDataByNewData(
        selectData,
        "geometry",
        updSelectData.geometryOptions
      )(state);
    }

    if (updSelectData.pathObjectIds) {
      resData.pathObjectIds = [...updSelectData.pathObjectIds];
    }

    if (updSelectData.glbData) {
      resData.glbData = {
        ...resData.glbData,
        ...updSelectData.glbData,
      };
    }

    return resData;
  };

export const getUpdateSelectOptionsDataByNewData =
  (
    selectData: ISelectItemObjectData,
    type: optionObjectOptionT,
    data: ISelectObjectOptions
  ) =>
  (state: stateT): ISelectObjectOptions => {
    const copyData: ISelectItemObjectData = deepCopy(selectData);

    const currentOptions =
      type === "material" ? copyData.materialOptions : copyData.geometryOptions;

    Object.keys(data).forEach(proxyId => {
      const proxyIdNum = parseInt(proxyId, 10);
      // Enforce single option family per proxy: reset other optionIds for this proxy
      currentOptions[proxyIdNum] = {};

      Object.keys(data[proxyIdNum]).forEach(optionId => {
        const optionIdNum = parseInt(optionId, 10);
        const keysOptionsProxyOther = Object.keys(
          currentOptions[proxyIdNum]
        ).filter(key => key !== optionId);

        keysOptionsProxyOther.forEach(optionIdOther => {
          const optionIdOtherNum = parseInt(optionIdOther, 10);
          currentOptions[proxyIdNum][optionIdOtherNum] = -1;
        });

        currentOptions[proxyIdNum][optionIdNum] = data[proxyIdNum][optionIdNum];
      });
    });

    return currentOptions;
  };
